<template>
  <v-dialog
    v-model="value"
    persistent
  >
    <v-card>
      <v-card-title class="headline">
        {{ strings.setLeakLocation}}
      </v-card-title>
      <v-container>
        <v-layout>
          <v-flex v-if="!loading && marker">
            <GmapMap
              ref="mapRef"
              :center="mapDetails.center"
              :heading="mapDetails.heading"
              :options="mapDetails"
              :tilt="mapDetails.tilt"
              :zoom="mapDetails.zoom"
              class="mini-map"
            >
              <GmapMarker
                v-if="marker"
                :position="marker.googleLocation"
                :clickable="marker.clickable"
                :draggable="marker.draggable"
                :icon="marker.icon"
                :animation="marker.animation"
                :attribution="marker.attribution"
                :cursor="marker.cursor"
                :label="marker.label"
                :opacity="marker.opacity"
                :options="marker.options"
                :place="marker.place"
                :shape="marker.shape"
                :title="marker.title"
                :z-index="marker.zIndex"
                @click="onMarkerClick($event, marker)"
                @dblclick="onMarkerDblClick($event, marker)"
                @drag="onMarkerDrag($event, marker)"
                @dragend="onMarkerDragEnd($event, marker)"
                @dragstart="onMarkerDragStart($event, marker)"
                @mousedown="onMarkerMouseDown($event, marker)"
                @mouseout="onMarkerMouseOut($event, marker)"
                @mouseover="onMarkerMouseOver($event, marker)"
                @mouseup="onMarkerMouseUp($event, marker)"
                @rightclick="onMarkerRightClick($event, marker)"
                @position_changed="onMarkerPositionChanged($event, marker)"
              />
            </GmapMap>
          </v-flex>
          <v-flex v-else>
            <v-progress-circular
              indeterminate
              color="secondary"
            />
          </v-flex>
        </v-layout>
      </v-container>
      <v-card-actions>
        <v-spacer />
        <v-btn
          flat
          @click="setMarkerGPS"
        >
          {{ strings.gPS }}
        </v-btn>
        <v-btn
          flat
          @click="close(true)"
        >
          {{ strings.cancel }}
        </v-btn>
        <v-btn
          flat
          @click="close"
        >
          {{ strings.doneButton }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { gmapApi } from "vue2-google-maps";
import { GenericMarkerData } from "@primayer/prime-google-map";
import { mapGetters } from "vuex";
export default {
  name: "LocationDialog",
  props: ["value", "poiCenter"],
  data() {
    return {
      marker: null,
      loading: false,
      centerCoords: null
    };
  },
  computed: {
    ...mapGetters("page", ["strings"]),
    google: gmapApi,
    mapDetails() {
      if (this.mapOptions) {
        return this.mapOptions;
      } else {
        //no map options so make an object with sensible defaults
        let centerCoords = null;
        if (this.centerCoords !== null) {
          centerCoords = this.centerCoords;
        } else {
          centerCoords = {
            lat: 50.895229,
            lng: -1.069013
          };
        }
        let opt = {
          center: this.poiCenter,
          heading: 0,
          disableDefaultUI: true,
          fullscreenControl: true,
          styles: [
            {
              featureType: "poi",
              elementType: "labels",
              stylers: [{ visibility: "off" }]
            }
          ],
          draggableCursor: this.mapCursor,
          tilt: 0,
          zoom: 15
        };
        return opt;
      }
    }
  },
  watch: {
    value(value) {
      if (value) {
        this.setCenterByLocation();
      }
    }
  },
  mounted() {
    this.setup();
  },
  methods: {
    setup() {
      this.centerCoords = this.poiCenter;
    },
    setLoading(value) {
      this.loading = value;
    },
    close(event, cancel) {
      if (cancel) {
        this.marker = null;
      }
      if (this.marker !== null && this.marker !== undefined) {
        this.$emit("leakMarkerSet", this.marker);
      }
      this.$emit("input", false);
    },
    makeMarker(pos) {
      let markerSetup = {
        latitude: pos.latitude,
        longitude: pos.longitude,
        clickable: true,
        draggable: true,
        zIndex: 9
      };
      this.marker = new GenericMarkerData(markerSetup);
    },
    setMarkerGPS() {
      let location = this.getLocation();
      location
        .then(pos => {
          this.makeMarker(pos);
        })
    },
    getLocation() {
      if (navigator.geolocation) {
        return new Promise(function(resolve, reject) {
          navigator.geolocation.getCurrentPosition(
            position => {
              resolve(position.coords);
            },
            error => {
              reject(error);
            },
            { maximumAge: 10000, timeout: 5000, enableHighAccuracy: true }
          );
        });
      } else {
        return null;
      }
    },
    convertBrowserCorrdsToGoogleCoords(coords) {
      return {
        lat: coords.latitude,
        lng: coords.longitude
      };
    },
    setCenterByLocation() {
      let location = this.getLocation();
      this.setLoading(true);
      location
        .then(pos => {
          this.gpsPosition = pos;
          // set center
          this.centerCoords = this.convertBrowserCorrdsToGoogleCoords(pos);
          //make and set new marker.
          this.makeMarker({
            latitude: this.poiCenter.lat,
            longitude: this.poiCenter.lng
          });
          this.setLoading(false);
        })
        .catch(error => {
          this.gpsError = error;
          // set the makrers location to the poi center (sencible default)
          this.makeMarker({
            latitude: this.poiCenter.lat,
            longitude: this.poiCenter.lng
          });
          this.setLoading(false);
        });
    },
    // event bypassers: Markers
    onMarkerClick(e, marker) {
      this.$emit("marker-click", e, marker);
    },
    onMarkerDblClick(e, marker) {
      this.$emit("marker-dblclick", e, marker);
    },
    onMarkerDrag(e, marker) {
      this.$emit("marker-drag", e, marker);
    },
    onMarkerDragEnd(e, marker) {
      this.$emit("marker-dragend", e, marker);
    },
    onMarkerDragStart(e, marker) {
      this.$emit("marker-dragstart", e, marker);
    },
    onMarkerMouseDown(e, marker) {
      this.$emit("marker-mousedown", e, marker);
    },
    onMarkerMouseOut(e, marker) {
      this.$emit("marker-mouseout", e, marker);
    },
    onMarkerMouseOver(e, marker) {
      this.$emit("marker-mouseover", e, marker);
    },
    onMarkerMouseUp(e, marker) {
      this.$emit("marker-mouseup", e, marker);
    },
    onMarkerRightClick(e, marker) {
      this.$emit("marker-rightclick", e, marker);
    },
    onMarkerPositionChanged(e, marker) {
      marker.latitude = e.lat();
      marker.longitude = e.lng();
      this.$emit("marker-position-changed", e, marker);
    }
  }
};
</script>

<style lang="less" scoped>
.vue-map-container.mini-map {
  height: 50vh;
  width: 100%;
}
</style>
